import { cn } from "../../../lib/utils.mjs";
import * as React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

//#region src/components/ui/retail-storefront/button.tsx
const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0", {
	variants: {
		variant: {
			default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
			destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
			outline: "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
			secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
			ghost: "hover:bg-accent hover:text-accent-foreground",
			link: "text-primary underline-offset-4 hover:underline"
		},
		size: {
			default: "h-9 px-4 py-2",
			sm: "h-8 rounded-md px-3 text-xs",
			lg: "h-10 rounded-md px-8",
			icon: "h-9 w-9"
		}
	},
	defaultVariants: {
		variant: "default",
		size: "default"
	}
});
/**
* Spinner component for loading state
*/
const Spinner = ({ className }) => /* @__PURE__ */ jsxs("svg", {
	className: cn("animate-spin", className),
	xmlns: "http://www.w3.org/2000/svg",
	fill: "none",
	viewBox: "0 0 24 24",
	children: [/* @__PURE__ */ jsx("circle", {
		className: "opacity-25",
		cx: "12",
		cy: "12",
		r: "10",
		stroke: "currentColor",
		strokeWidth: "4"
	}), /* @__PURE__ */ jsx("path", {
		className: "opacity-75",
		fill: "currentColor",
		d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
	})]
});
const Button = React.forwardRef(({ className, variant, size, asChild = false, loading = false, debounceMs = 300, children, disabled, onClick, ...props }, ref) => {
	const Comp = asChild ? Slot : "button";
	const isIconButton = size === "icon";
	const lastClickRef = React.useRef(0);
	const handleClick = React.useCallback((event) => {
		if (debounceMs > 0) {
			const now = typeof performance !== "undefined" ? performance.now() : Date.now();
			if (now - lastClickRef.current < debounceMs) return;
			lastClickRef.current = now;
		}
		onClick?.(event);
	}, [debounceMs, onClick]);
	return /* @__PURE__ */ jsxs(Comp, {
		className: cn(buttonVariants({
			variant,
			size,
			className
		})),
		ref,
		disabled: disabled || loading,
		onClick: handleClick,
		...props,
		children: [loading && /* @__PURE__ */ jsx(Spinner, { className: cn("h-4 w-4", isIconButton ? "" : "-ml-1") }), isIconButton && loading ? null : children]
	});
});
Button.displayName = "Button";

//#endregion
export { Button, Spinner, buttonVariants };
//# sourceMappingURL=button.mjs.map